import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  BucksCounter,
  CoachMarks,
  EmptyState,
  GroupCard,
  GroupMemberRow,
  HiddenCountRow,
  MysteryQuestCard,
  QuestCard,
} from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { Translator } from "@ec2/i18n";
import type { QuestView } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import {
  CATEGORY_LABEL_KEY,
  doneCount,
  groupBonusCaption,
  groupedForTab,
  uiCategory,
  uiState,
  type FilterTab,
} from "../lib/quests";
import { formatClock, formatRelative } from "../lib/time";
import { useActive, useQuestList } from "../state/hooks";
import { useSession } from "../state/session";
import { Shell } from "../components/Shell";
import { EventCountdownCard } from "../components/EventCountdownCard";
import { PullToRefresh } from "../components/PullToRefresh";
import { BucksLedgerSheet } from "../components/BucksLedgerSheet";
import { LoadingLine } from "../components/common";

export function questMeta(
  quest: QuestView,
  t: Translator,
  locale: "en" | "de",
  timezone: string | undefined,
  now: Date = new Date(),
): string {
  const category = t(CATEGORY_LABEL_KEY[uiCategory(quest.category)]);
  const state = uiState(quest, now);
  if (state === "expired") return `${category} · ${t("ec2.quest.meta.expired")}`;
  if (state === "locked" && quest.startsAt) {
    return `${category} · ${t("ec2.quest.meta.startsAt", { time: formatClock(quest.startsAt, locale, timezone) })}`;
  }
  const parts = [category];
  if (quest.endsAt) parts.push(t("ec2.quest.meta.endsAt", { time: formatClock(quest.endsAt, locale, timezone) }));
  if (quest.maxCompletions !== null) {
    parts.push(t("ec2.quest.meta.slotsLeft", { count: Math.max(0, quest.maxCompletions - quest.completionsCount) }));
  }
  return parts.join(" · ");
}

function failedReason(quest: QuestView, t: Translator): string | undefined {
  const { correctAnswers, totalQuestions } = quest.completion ?? {};
  // The pass mark is per event now, so the label reports what was achieved
  // rather than restating a threshold this screen cannot know.
  if (correctAnswers != null && totalQuestions != null) {
    return t("ec2.quest.meta.quizScore", { correct: correctAnswers, total: totalQuestions });
  }
  return undefined;
}

/** Collapsed group ids, persisted per event for the session (default: expanded). */
function useCollapsedGroups(eventId: string | undefined) {
  const key = `ec-groups-collapsed:${eventId ?? ""}`;
  const [collapsed, setCollapsed] = useState<string[]>([]);
  // Re-read when the participation (and thus the key) becomes known.
  useEffect(() => {
    try {
      setCollapsed(JSON.parse(sessionStorage.getItem(key) ?? "[]") as string[]);
    } catch {
      setCollapsed([]);
    }
  }, [key]);
  const toggle = (groupId: string) => {
    setCollapsed((current) => {
      const next = current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId];
      try {
        sessionStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage full/blocked — collapse state is a nicety */
      }
      return next;
    });
  };
  return { collapsed, toggle };
}

/** Home (brief §6): header w/ balance, All·Open·Done filters (Open default), mystery card. */
export function QuestsScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { locale } = useSession();
  const { active, user, isLoading, me } = useActive();
  // Tour step 5 quotes this event's reward per connection.
  const contactBucks = active?.event.contactBucksReward ?? 0;
  const quests = useQuestList(active?.eventId);
  const [tab, setTab] = useState<FilterTab>("open");
  const search = useSearch({ strict: false }) as { tour?: number };
  const trpc = useTRPC();
  const completeTour = useMutation(trpc.participant.completeTour.mutationOptions());
  const [tourStep, setTourStep] = useState(0);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const groupCollapse = useCollapsedGroups(active?.eventId);
  const tourOpen = Boolean(search.tour) && Boolean(active);

  useEffect(() => {
    if (!isLoading && !active) void navigate({ to: "/join" });
  }, [isLoading, active, navigate]);

  const endTour = () => {
    if (user?.tourCompletedAt === null) {
      completeTour.mutate(undefined, { onSettled: () => void me.refetch() });
    }
    setTourStep(0);
    void navigate({ to: "/quests", search: {}, replace: true });
  };

  const list = quests.data;
  const now = new Date();
  const sections = list ? groupedForTab(list, tab, now) : { groups: [], ungrouped: [] };
  const done = list ? doneCount(list) : 0;
  const empty = sections.groups.length === 0 && sections.ungrouped.length === 0;
  // Joined before the start: a countdown instead of quests (the server lists none yet).
  const notStarted = active ? active.event.startsAt > now : false;

  const tabs: { id: FilterTab; label: string }[] = [
    { id: "all", label: t("ec2.filter.all") },
    { id: "open", label: t("ec2.filter.open") },
    { id: "done", label: done > 0 ? t("ec2.filter.doneCount", { count: done }) : t("ec2.filter.done") },
  ];

  return (
    <Shell active="quests">
      <PullToRefresh onRefresh={() => Promise.all([quests.refetch(), me.refetch()])}>
        <header className="flex items-center justify-between px-4 pt-4">
          <span className="font-display text-lg font-bold tracking-[-0.03em] text-fg">{t("ec2.common.appName")}</span>
          <BucksCounter
            value={active?.bucksBalance ?? 0}
            label={t("ec2.a11y.bucksBalance", { amount: active?.bucksBalance ?? 0 })}
            locale={locale}
            onClick={active ? () => setLedgerOpen(true) : undefined}
          />
        </header>
        {active && (
          <p className="px-4 pb-3 text-[13px] font-medium text-fg-secondary">
            {active.event.name} · {t("ec2.home.endsIn", { time: formatRelative(active.event.endsAt, locale, now) })}
          </p>
        )}

        <div role="tablist" aria-label={t("ec2.nav.quests")} className="flex gap-2 px-4 pb-3" hidden={notStarted}>
          {tabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              onClick={() => setTab(entry.id)}
              data-testid={`ec-filter-${entry.id}`}
              className={
                `rounded-(--radius-chip) px-3.5 py-1.5 text-[13px] font-semibold tabular-nums ` +
                `focus-visible:outline-2 focus-visible:outline-primary ` +
                (tab === entry.id ? "bg-primary text-primary-fg" : "bg-surface-overlay text-fg-secondary")
              }
            >
              {entry.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 px-4" data-testid="ec-quest-list">
          {quests.isLoading && <LoadingLine />}
          {notStarted && active && (
            <EventCountdownCard
              startsAt={active.event.startsAt}
              timeZone={active.event.timezone}
              locale={locale}
              onStarted={() => void Promise.all([quests.refetch(), me.refetch()])}
            />
          )}
          {list && empty && tab !== "done" && !notStarted && (
            <EmptyState title={t("ec2.empty.quests.title")} body={t("ec2.empty.quests.body")} />
          )}

          {sections.groups.map(({ group, quests: members }) => (
            <GroupCard
              key={group.id}
              icon={group.icon}
              name={group.name}
              totalQuests={group.totalQuests}
              completedCount={group.completedCount}
              neededCount={group.neededCount}
              bonusBucks={group.bonusBucks}
              bonusAwarded={group.bonusAwarded}
              bonusCaption={groupBonusCaption(group, t)}
              bonusLabel={t("ec2.a11y.bucksReward", { amount: group.bonusBucks })}
              countLabel={t("ec2.a11y.groupProgress", { done: group.completedCount, total: group.totalQuests })}
              collapsed={groupCollapse.collapsed.includes(group.id)}
              onToggle={() => groupCollapse.toggle(group.id)}
            >
              {members.map((quest) => {
                const state = uiState(quest, now);
                const category = uiCategory(quest.category);
                return (
                  <GroupMemberRow
                    key={quest.id}
                    state={state}
                    category={category}
                    categoryLabel={t(CATEGORY_LABEL_KEY[category])}
                    title={quest.name}
                    meta={state === "active" ? questMeta(quest, t, locale, active?.event.timezone, now) : undefined}
                    reward={quest.bucksReward}
                    rewardLabel={t("ec2.a11y.bucksReward", { amount: quest.bucksReward })}
                    failedReason={failedReason(quest, t)}
                    solvedLabel={t("ec2.a11y.solved")}
                    failedLabel={t("ec2.a11y.failed")}
                    onClick={() => void navigate({ to: "/quests/$questId", params: { questId: quest.id } })}
                  />
                );
              })}
              {tab !== "done" && group.hiddenUncompletedCount > 0 && (
                <HiddenCountRow
                  title={
                    group.hiddenUncompletedCount === 1
                      ? t("ec2.group.hidden.titleOne")
                      : t("ec2.group.hidden.title", { count: group.hiddenUncompletedCount })
                  }
                  caption={t("ec2.group.hidden.caption")}
                  onClick={() => void navigate({ to: "/scan" })}
                />
              )}
            </GroupCard>
          ))}

          {sections.groups.length > 0 && sections.ungrouped.length > 0 && (
            <p className="pt-1 text-[13px] font-semibold text-fg-muted">{t("ec2.quest.section.more")}</p>
          )}
          {sections.ungrouped.map((quest) => {
            const state = uiState(quest, now);
            const category = uiCategory(quest.category);
            return (
              <QuestCard
                key={quest.id}
                state={state}
                category={category}
                categoryLabel={t(CATEGORY_LABEL_KEY[category])}
                title={quest.name}
                meta={questMeta(quest, t, locale, active?.event.timezone, now)}
                reward={quest.bucksReward}
                rewardLabel={t("ec2.a11y.bucksReward", { amount: quest.bucksReward })}
                processingText={t("ec2.quest.processing.eta", { seconds: 30 })}
                processingCaption={t("ec2.quest.processing.caption")}
                earned={quest.bucksReward}
                failedReason={failedReason(quest, t)}
                solvedLabel={t("ec2.a11y.solved")}
                failedLabel={t("ec2.a11y.failed")}
                onClick={() => void navigate({ to: "/quests/$questId", params: { questId: quest.id } })}
              />
            );
          })}
          {tab !== "done" && list && !notStarted && (
            <MysteryQuestCard
              title={t("ec2.quest.mystery.title")}
              caption={t("ec2.quest.mystery.caption")}
              rewardLabel={t("ec2.a11y.mysteryReward")}
              onClick={() => void navigate({ to: "/scan" })}
            />
          )}
        </div>
      </PullToRefresh>

      {ledgerOpen && active && (
        <BucksLedgerSheet
          eventId={active.eventId}
          timezone={active.event.timezone}
          onClose={() => setLedgerOpen(false)}
        />
      )}

      <CoachMarks
        open={tourOpen}
        stepIndex={tourStep}
        steps={[1, 2, 3, 4, 5].map((step) => ({
          target: () => document.querySelector(TOUR_TARGETS[step - 1]!),
          title: t(`ec2.tour.${step}.title` as "ec2.tour.1.title"),
          // Step 5 (contacts) quotes THIS event's reward per connection, not a fixed number.
          body:
            step === 5
              ? contactBucks > 0
                ? t("ec2.tour.5.body", { bucks: contactBucks })
                : t("ec2.tour.5.bodyNoBucks")
              : t(`ec2.tour.${step}.body` as "ec2.tour.1.body"),
          radius: step === 1 ? 20 : 12,
        }))}
        labels={{
          skip: t("ec2.tour.skip"),
          next: t("ec2.tour.next"),
          done: t("ec2.tour.letsPlay"),
          step: t("ec2.tour.step", { current: tourStep + 1, total: 5 }),
        }}
        onNext={() => setTourStep((s) => Math.min(s + 1, 4))}
        onSkip={endTour}
        onDone={endTour}
      />
    </Shell>
  );
}

/** Spotlight targets (§1.6): quest card → Scan button → balance → Shop tab → Profile tab. */
const TOUR_TARGETS = [
  '[data-testid="ec-quest-card"], [data-testid="ec-mystery-card"], [data-testid="ec-countdown-card"]',
  '[data-testid="ec-nav-scan"]',
  '[data-testid="ec-bucks-counter"]',
  '[data-testid="ec-nav-shop"]',
  '[data-testid="ec-nav-profile"]',
];
